import React, { useEffect, useState } from 'react';
import { useCookieTracking } from '../../hooks/useCookieTracking';
import { isTrackingBlocked, safeLogError, safeStorage, safeCookies } from '../../utils/trackingUtils';// import { logger } from "../../utils/logger";
interface CookieTrackingProviderProps {
  children: React.ReactNode;
}

const CookieTrackingProvider: React.FC<CookieTrackingProviderProps> = ({ children }) => {
  const [trackingEnabled, setTrackingEnabled] = useState(true);
  const [storageBlocked, setStorageBlocked] = useState(false);

  // Initialize enhanced cookie tracking within Router context
  const { trackInteraction, trackPerformance } = useCookieTracking({
    trackPageViews: trackingEnabled && !storageBlocked,
    trackUserInteractions: trackingEnabled && !storageBlocked,
    enableTrainingProgress: trackingEnabled && !storageBlocked,
    trackSearchQueries: trackingEnabled && !storageBlocked,
    trackSessionEvents: trackingEnabled && !storageBlocked,
    autoCreateSession: trackingEnabled && !storageBlocked
  });

  // Initialize enhanced tracking after Router is available
  useEffect(() => {
    if (!trackingEnabled) return;

    try {
      // Check for tracking prevention before attempting any operations
      if (isTrackingBlocked()) {
        // Silently disable tracking without console logs
        setTrackingEnabled(false);
        setStorageBlocked(true);
        
        // Store tracking state in safe storage for future reference
        safeStorage.setItem('trackingDisabled', 'true');
        safeStorage.setItem('trackingDisabledReason', 'browser_privacy');
        safeStorage.setItem('trackingDisabledTimestamp', new Date().toISOString());
        
        return;
      }

      // Track app initialization with enhanced tracking
      trackInteraction('app_initialized', {
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        context: 'app_initialization'
      });

      // Track performance metrics
      if ('performance' in window) {
        const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (perfData) {
          trackPerformance('page_load_time', perfData.loadEventEnd - perfData.loadEventStart, 'ms');
          trackPerformance('dom_content_loaded', perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart, 'ms');
        }
      }
    } catch (error) {
      // Use safe logging to only show non-tracking-prevention errors
      safeLogError('Failed to initialize tracking:', error);
      
      // Check if this is a tracking prevention error and handle silently
      if (error instanceof DOMException && (
        error.name === 'SecurityError' ||
        error.message.includes('storage') ||
        error.message.includes('blocked') ||
        error.message.includes('quota') ||
        error.message.includes('permission')
      )) {
        // Silently disable tracking without console warnings
        setTrackingEnabled(false);
        setStorageBlocked(true);
        
        // Store tracking state in safe storage
        safeStorage.setItem('trackingDisabled', 'true');
        safeStorage.setItem('trackingDisabledReason', 'security_error');
        safeStorage.setItem('trackingDisabledTimestamp', new Date().toISOString());
        
        return;
      }
    }
  }, [trackInteraction, trackPerformance, trackingEnabled]);

  return <>{children}</>;
};

export default CookieTrackingProvider; 
